import React, { useContext, useReducer, useEffect, useState } from 'react'
import { Box, Card } from 'pcln-design-system'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import StyledHeading from '@/components/StyledHeading'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import useSEMSearchHeadline from '@/hooks/useSEMSearchHeadline'
import CONTENT, { GREETING_DESKTOP } from '@/constants/content'
import useCityNameInFormHeadlineForSEM from '@/hooks/useCityNameInFormHeadlineForSEM'
import useContentfulSearchFormHeadlines from '@/hooks/useContentfulSearchFormHeadlines'
import { LOCATION_SEARCH_TYPE } from '@/types'
import useSeti from '@/hooks/useSeti'
import reducer, { formPicker } from './reducer'
import { HEADERS } from './constants'
import SearchFormProvider from '../../context/SearchFormContext'
import ConditionalRender, { Experiment, Original } from '../ConditionalRender'
import Tabs from './Tabs'

const StyledCard = styled(Card)`
  border-radius: 24px 24px 6px 6px;
`

const getHeadlineWithFirstName = (template: string, firstName: string) =>
  firstName ? template.replace('{firstName}', firstName) : GREETING_DESKTOP

export default function SearchForm() {
  const { query } = useRouter()
  const currentTab =
    Object.keys(query).length && typeof query.tab === 'string' && query.tab
      ? query.tab
      : 'hotels'

  const {
    customer: { firstName },
    signInStatus: { signedIn, passiveSignIn }
  } = useContext(BootstrapDataContext)
  const isSignedIn = signedIn || passiveSignIn
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const prePopHeadline = useCityNameInFormHeadlineForSEM(location)
  const SEMHeadline = useSEMSearchHeadline()
  const VALUES = {
    hotels:
      useContentfulSearchFormHeadlines(
        'searchFormHotels',
        currentTab === 'hotels'
      ) || HEADERS.hotels,
    flights:
      useContentfulSearchFormHeadlines(
        'searchFormFlights',
        currentTab === 'flights'
      ) || HEADERS.flights,
    vacations:
      useContentfulSearchFormHeadlines(
        'searchFormVacations',
        currentTab === 'vacations'
      ) || HEADERS.vacations,
    cars:
      useContentfulSearchFormHeadlines(
        'searchFormCars',
        currentTab === 'cars'
      ) || HEADERS.cars,
    cruises:
      useContentfulSearchFormHeadlines(
        'searchFormCruises',
        currentTab === 'cruises'
      ) || HEADERS.cruises
  }

  const defaultHeadline =
    prePopHeadline || SEMHeadline || VALUES.hotels.headline
  /**
   * TODO: Add these values to contentful and add fallback values as well
   */

  const headlineSeti = useSeti('HP_SIGNEDIN_HEADINGS')

  const headlineWithDefault =
    headlineSeti === 'DEFAULT' || headlineSeti === 'CONTROL'
      ? getHeadlineWithFirstName(
          CONTENT.homepage.vip.headlineWithName,
          firstName
        )
      : ''
  const headlineWithQualify =
    headlineSeti === 'QUALIFY'
      ? getHeadlineWithFirstName(
          CONTENT.homepage.vip.headlineWithNameQualify,
          firstName
        )
      : ''
  const headlineWithBigDeal =
    headlineSeti === 'BIG_DEAL'
      ? getHeadlineWithFirstName(
          CONTENT.homepage.vip.headlineWithNameBigDeal,
          firstName
        )
      : ''

  const hotelHeadline = !isSignedIn
    ? defaultHeadline
    : headlineWithQualify || headlineWithBigDeal || headlineWithDefault

  const [state, dispatch] = useReducer(
    reducer,
    formPicker(VALUES, currentTab, hotelHeadline, setLocation)
  )

  useEffect(() => {
    dispatch({
      values: VALUES,
      type: currentTab,
      vipHead: hotelHeadline,
      setLocation
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, hotelHeadline])

  return (
    <ConditionalRender experimentCode="HP_DT_HEADING_PLACEMENT">
      <Experiment>
        <Box>
          <StyledHeading
            pl={3}
            pb={[16, null, null, null, 4]}
            pr={[0, null, null, null, '130px']}
            tag="h1"
            color="text.heading"
            text={state.headline}
            mx={[4, null, null, null, 0]}
            textStyle={[
              'heading2',
              'heading3',
              null,
              'heading2',
              null,
              'heading1'
            ]}
          />
          <Card
            boxShadowSize="xl"
            borderWidth={0}
            borderRadius="2xl"
            minWidth={[null, null, null, '720px']}
            color="background.lightest"
            mx={[4, null, null, null, 0]}
          >
            <StyledCard boxShadowSize="none" borderWidth={0}>
              <StyledHeading
                p={3}
                tag="h2"
                color="text.light"
                text=""
                textStyle={[
                  'subheading6',
                  'subheading4',
                  null,
                  null,
                  'subheading4'
                ]}
              />
              <Tabs
                onClick={(name: string) => {
                  dispatch({
                    values: VALUES,
                    type: name,
                    vipHead: hotelHeadline,
                    setLocation
                  })
                }}
              />
            </StyledCard>
            <SearchFormProvider>
              <Box
                p={3}
                id={`panel-${currentTab}`}
                aria-labelledby={`tab-${currentTab}`}
              >
                {state.component}
              </Box>
              <Box>{state.bumper}</Box>
            </SearchFormProvider>
          </Card>
        </Box>
      </Experiment>
      <Original>
        <Card
          boxShadowSize="xl"
          borderWidth={0}
          borderRadius="2xl"
          minWidth={[null, null, null, '720px']}
          color="background.lightest"
          mx={[4, null, null, null, 0]}
        >
          <StyledCard boxShadowSize="none" borderWidth={0}>
            <StyledHeading
              p={3}
              pb={1}
              tag="h1"
              color="text.heading"
              text={state.headline}
              textStyle={['heading3', null, null, null, 'heading2']}
            />
            <StyledHeading
              px={3}
              pb={3}
              tag="h2"
              color="text.light"
              text=""
              textStyle={['subheading6', null, null, null, 'subheading4']}
            />
            <Tabs
              onClick={(name: string) => {
                dispatch({
                  values: VALUES,
                  type: name,
                  vipHead: hotelHeadline,
                  setLocation
                })
              }}
            />
          </StyledCard>
          <SearchFormProvider>
            <Box
              p={3}
              id={`panel-${currentTab}`}
              aria-labelledby={`tab-${currentTab}`}
            >
              {state.component}
            </Box>
            <Box>{state.bumper}</Box>
          </SearchFormProvider>
        </Card>
      </Original>
    </ConditionalRender>
  )
}
